'use client';

import { useTranslation } from 'react-i18next';

/**
 * Picks the locale-appropriate string from a pair of (default, _ar) fields
 * authored by the tenant in the home-screen-builder.
 *
 * Authors fill the default in the primary editing language (English by
 * default for this codebase) and optionally fill an `*_ar` companion. When
 * the active i18n locale is `ar` and `valueAr` is non-empty, we return it;
 * otherwise we fall back to the default. So leaving translations blank
 * never breaks the UI — it just shows the original copy.
 *
 *   const headline = useLocalized(slide.headline, slide.headline_ar);
 */
export function useLocalized<T extends string | undefined>(
  value: T,
  valueAr: string | undefined,
): T {
  const { i18n } = useTranslation();
  if (i18n.language?.split('-')[0] === 'ar' && valueAr && valueAr.trim() !== '') {
    return valueAr as T;
  }
  return value;
}

/**
 * Non-hook variant for places that already have an i18n instance / locale
 * string in scope (e.g. iterating over a list of items inside a render).
 */
export function pickLocalized(
  value: string | undefined,
  valueAr: string | undefined,
  locale: string,
): string | undefined {
  if (locale?.split('-')[0] === 'ar' && valueAr && valueAr.trim() !== '') {
    return valueAr;
  }
  return value;
}
